import type {
  Behavior,
  FieldContent,
  FormContent,
  JobTypeVersionContentV3,
  Stage,
} from '@capture/shared';

/** Look up a form + one of its fields by id/name. Returns null if either is absent. */
function findFormAndField(
  contentV3: JobTypeVersionContentV3 | null,
  formId: string,
  fieldName: string,
): { form: FormContent; field: FieldContent } | null {
  const form = contentV3?.forms.find((f) => f.id === formId);
  if (!form) return null;
  const field = form.fields.find((f) => f.name === fieldName);
  if (!field) return null;
  return { form, field };
}

/**
 * The next `fields` array for `formId` with `behavior` appended to
 * `fieldName`'s `stageMap[stage]`. Immutable; returns null if the form or
 * field is absent. Caller passes the result to the store's
 * `updateFormFields(formId, next)` (id-scoped — no activeForm side effect).
 */
export function fieldsWithAppendedBehavior(
  contentV3: JobTypeVersionContentV3 | null,
  formId: string,
  fieldName: string,
  stage: Stage,
  behavior: Behavior,
): FieldContent[] | null {
  const r = findFormAndField(contentV3, formId, fieldName);
  if (!r) return null;
  return r.form.fields.map((f) =>
    f.name === fieldName
      ? { ...f, stageMap: { ...f.stageMap, [stage]: [...(f.stageMap[stage] ?? []), behavior] } }
      : f,
  );
}

/**
 * The next `fields` array for `formId` with the behavior at `index` within
 * `fieldName`'s `stageMap[stage]` REPLACED by `behavior`. Immutable; returns
 * null if the form/field/stage array is absent or `index` is out of range.
 */
export function fieldsWithReplacedBehavior(
  contentV3: JobTypeVersionContentV3 | null,
  formId: string,
  fieldName: string,
  stage: Stage,
  index: number,
  behavior: Behavior,
): FieldContent[] | null {
  const r = findFormAndField(contentV3, formId, fieldName);
  if (!r) return null;
  const arr = r.field.stageMap[stage];
  if (!arr || index < 0 || index >= arr.length) return null;
  return r.form.fields.map((f) =>
    f.name === fieldName
      ? { ...f, stageMap: { ...f.stageMap, [stage]: arr.map((b, i) => (i === index ? behavior : b)) } }
      : f,
  );
}

/**
 * The next `fields` array for `formId` with the behavior at `index` within
 * `fieldName`'s `stageMap[stage]` REMOVED. Immutable; returns null if the
 * form/field/stage array is absent or `index` is out of range.
 */
export function fieldsWithRemovedBehavior(
  contentV3: JobTypeVersionContentV3 | null,
  formId: string,
  fieldName: string,
  stage: Stage,
  index: number,
): FieldContent[] | null {
  const r = findFormAndField(contentV3, formId, fieldName);
  if (!r) return null;
  const arr = r.field.stageMap[stage];
  if (!arr || index < 0 || index >= arr.length) return null;
  return r.form.fields.map((f) =>
    f.name === fieldName
      ? { ...f, stageMap: { ...f.stageMap, [stage]: arr.filter((_, i) => i !== index) } }
      : f,
  );
}
